import { firestore } from "firebase/app";

import { db } from "../../main";
import { Panel, StatusHistory } from "../models/Panel";
import {
  RunOverview,
  getAdjustedEndDate,
  getInitialMeterReading,
  isActiveRun,
} from "@/data/models/RunOverview";
import { getClientId, getMemberClientIds } from "@/data/repository/ClientRepository";
import { PanelModule } from "@/store/modules/PanelModule";
import { areOnSameDay, toEndOfDay } from "@/utils/DateHelper";
import { closeRun, updateRun } from "./RunRepository";
import { ProgramChain } from "../models/ProgramChain";
import { filterOutUndefined } from "@/utils/Utils";
import {
  IresDevice,
  IresDeviceCategory,
  getIresDeviceCategoryFromSerialNumber,
  getIresDeviceCollectionName,
} from "../models/IresDevice";
import { updateComBox } from "./ComBoxRepository";
import { observeDevicesOfClients } from "./MemberClientDeviceObserver";

export interface MeterReadingInfo {
  meterReadingInKWh: number;
  date?: firestore.Timestamp;
  projectName?: string;
  changedBy?: string;
  ignoreCorrectedBy?: boolean;
  isInvalid?: boolean;
  canBeCorrected?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function updatePanel(serialNumber: string, update: any) {
  const ref = db.collection("panels").doc(serialNumber);

  return ref.set(update, { merge: true });
}

export async function getPanel(serialNumber: string, cacheAllowed?: boolean) {
  const ref = db.collection("panels").doc(serialNumber);

  if (cacheAllowed) {
    const cachedPanel = PanelModule.cachedPanel(serialNumber);

    if (cachedPanel) {
      return cachedPanel;
    }
  }

  const result = (await ref.get()).data() as Panel;

  PanelModule.cachePanel(result);

  return result;
}

export async function closeProgramChain(
  serialNumber: string,
  programChain: ProgramChain,
  date: Date,
  meterReadingEnd?: number
) {
  const lastRun = programChain.runs[programChain.runs.length - 1];
  const iresDeviceCategory = getIresDeviceCategoryFromSerialNumber(
    serialNumber
  );

  return Promise.all([
    iresDeviceCategory === IresDeviceCategory.PANEL
      ? updatePanel(serialNumber, {
          panelStatus: {
            ipuStatus: 5,
          },
        })
      : updateComBox(serialNumber, {
          comBoxStatus: {
            connectReason: 31,
            dryRunActive: false,
          },
        }),
    updateRun(
      serialNumber,
      lastRun.id,
      filterOutUndefined({
        endDate: date,
        latestMeterReading: meterReadingEnd,
        ...(iresDeviceCategory === IresDeviceCategory.COMBOX
          ? { activeRun: false }
          : {}),
      })
    ),
  ]);
}

export async function setPanelOffline(
  serialNumber: string,
  date: Date,
  meterReadingEnd?: number
) {
  const panel = await getPanel(serialNumber);

  if (!panel) {
    return null;
  }

  const panelStatus = panel.panelStatus;

  if (!panelStatus) {
    return;
  }

  return Promise.all([
    updatePanel(serialNumber, {
      panelStatus: {
        ipuStatus: 5,
      },
    }),
    closeRun(serialNumber, panelStatus.activeRunId, date, meterReadingEnd),
  ]);
}

export async function getFirstRunEndedBefore(
  serialNumber: string,
  timestamp: firestore.Timestamp
) {
  const result = await db
    .collection(getIresDeviceCollectionName(serialNumber))
    .doc(serialNumber)
    .collection("runs")
    .where("endDate", "<=", timestamp)
    .orderBy("endDate", "desc")
    .limit(1)
    .get();

  if (result.empty) {
    return null;
  } else {
    return result.docs[0].data() as RunOverview;
  }
}

export async function getFirstRunStartedAfter(
  serialNumber: string,
  timestamp: firestore.Timestamp
) {
  const result = await db
    .collection(getIresDeviceCollectionName(serialNumber))
    .doc(serialNumber)
    .collection("runs")
    .where("startDate", ">=", timestamp)
    .orderBy("startDate", "asc")
    .limit(1)
    .get();

  if (result.empty) {
    return null;
  } else {
    return result.docs[0].data() as RunOverview;
  }
}

export async function getFirstRunStartedBefore(
  serialNumber: string,
  timestamp: firestore.Timestamp
) {
  const result = await db
    .collection(getIresDeviceCollectionName(serialNumber))
    .doc(serialNumber)
    .collection("runs")
    .where("startDate", "<=", timestamp)
    .orderBy("startDate", "desc")
    .limit(1)
    .get();

  if (result.empty) {
    return null;
  } else {
    return result.docs[0].data() as RunOverview;
  }
}

export async function getPanelMeterReadingForDate(
  serialNumber: string,
  timestamp: firestore.Timestamp,
  beforeDate: boolean
) {
  const result = await db
    .collection(getIresDeviceCollectionName(serialNumber))
    .doc(serialNumber)
    .collection("statusHistory")
    .where("createdAt", beforeDate ? "<=" : ">=", timestamp)
    .orderBy("createdAt", beforeDate ? "desc" : "asc")
    .limit(1)
    .get();

  if (result.empty) {
    return null;
  } else {
    const doc = result.docs[0];

    return {
      meterReadingInKWh: (doc.get("totalPowerUsed") as number) / 1000,
      date: doc.get("createdAt") as firestore.Timestamp,
    };
  }
}

export async function getPanelStatusHistoryRange(
  serialNumber: string,
  startDate: firestore.Timestamp,
  endDate: firestore.Timestamp
) {
  const result = await db
    .collection(getIresDeviceCollectionName(serialNumber))
    .doc(serialNumber)
    .collection("statusHistory")
    .where("createdAt", ">=", startDate)
    .where("createdAt", "<=", endDate)
    .orderBy("createdAt", "asc")
    .get();
  
  if (result.empty) {
    return [];
  } else {
    return result.docs.map((doc) => doc.data()) as StatusHistory[];
  }
}

export async function getPanelStatusHistoryRangeWithoutEndDate(
  serialNumber: string,
  startDate: firestore.Timestamp
) {
  const result = await db
    .collection(getIresDeviceCollectionName(serialNumber))
    .doc(serialNumber)
    .collection("statusHistory")
    .where("createdAt", ">=", startDate)
    .orderBy("createdAt", "asc")
    .get();
  
  if (result.empty) {
    return [];
  } else {
    return result.docs.map((doc) => doc.data()) as StatusHistory[];
  }
}

function getLatestMeterReading(runOverview: RunOverview) {
  if (runOverview.latestMeterReading) {
    return runOverview.latestMeterReading;
  } else if (runOverview.initialMeterReading) {
    return runOverview.initialMeterReading + runOverview.power;
  } else {
    return null;
  }
}

export async function getMeterReadingInKWhForDate(
  iresDevice: IresDevice,
  timestamp: firestore.Timestamp,
  beforeDate: boolean
): Promise<null | MeterReadingInfo> {
  if (!iresDevice) {
    return null;
  }

  const serialNumber = iresDevice.serialNo;
  const iresDeviceCategory = getIresDeviceCategoryFromSerialNumber(
    serialNumber
  );

  if (beforeDate) {
    const firstRunStartedBefore = await getFirstRunStartedBefore(
      serialNumber,
      timestamp
    );

    if (firstRunStartedBefore) {
      const powerUsedInKWh = getLatestMeterReading(firstRunStartedBefore);

      if (powerUsedInKWh || powerUsedInKWh === 0) {
        return {
          meterReadingInKWh: powerUsedInKWh,
          date: firestore.Timestamp.fromDate(
            getAdjustedEndDate(firstRunStartedBefore)
          ),
        };
      }
    }
  } else {
    const firstRunStartedAfter = await getFirstRunStartedAfter(
      serialNumber,
      timestamp
    );

    if (firstRunStartedAfter) {
      const powerUsedInKWh = getLatestMeterReading(firstRunStartedAfter);

      if (powerUsedInKWh || powerUsedInKWh === 0) {
        return {
          meterReadingInKWh: powerUsedInKWh,
          date: firstRunStartedAfter.startDate,
        };
      }
    }

    const firstRunBefore = await getFirstRunEndedBefore(
      serialNumber,
      timestamp
    );

    if (firstRunBefore && !isActiveRun(firstRunBefore)) {
      const powerUsedInKWh = firstRunBefore.latestMeterReading;

      if (powerUsedInKWh || powerUsedInKWh === 0) {
        return {
          meterReadingInKWh: powerUsedInKWh,
          date: firstRunBefore.endDate,
        };
      }
    }
  }

  if (iresDeviceCategory === IresDeviceCategory.PANEL) {
    const panel = iresDevice as Panel;

    if (panel.panelStatus) {
      const isPanelStatusRecent = areOnSameDay(
        panel.panelStatus.createdAt,
        timestamp
      );

      if (isPanelStatusRecent) {
        const totalPowerUsed = panel.panelStatus.totalPowerUsed;

        if (totalPowerUsed || totalPowerUsed === 0) {
          return {
            meterReadingInKWh: totalPowerUsed / 1000,
            date: panel.panelStatus.createdAt,
          };
        }
      }
    }
  }

  const endOfDay = toEndOfDay(timestamp);
  const firstRun = await (beforeDate
    ? getFirstRunEndedBefore(serialNumber, endOfDay)
    : getFirstRunStartedBefore(serialNumber, endOfDay));

  if (firstRun) {
    const adjustedEndDate = getAdjustedEndDate(firstRun);

    if (beforeDate || adjustedEndDate.getTime() < timestamp.toMillis()) {
      const powerUsedInKWh = firstRun.latestMeterReading;

      if (powerUsedInKWh || powerUsedInKWh === 0) {
        return {
          meterReadingInKWh: powerUsedInKWh,
          date: firestore.Timestamp.fromDate(adjustedEndDate),
        };
      }
    } else {
      const powerUsedInKWh = getInitialMeterReading(firstRun);

      if (powerUsedInKWh || powerUsedInKWh === 0) {
        return {
          meterReadingInKWh: powerUsedInKWh,
          date: firstRun.startDate,
        };
      }
    }
  }

  if (iresDeviceCategory === IresDeviceCategory.PANEL) {
    const panel = iresDevice as Panel;
    return await getPanelMeterReadingForDate(panel.serialNo, timestamp, true);
  } else {
    return null;
  }
}

export function observePanelsAssignedToMembers(
  onNext: (panels: Array<Panel>) => void
) {
  const memberClientIds = getMemberClientIds();
  const memberClientIdSet = new Set(memberClientIds);

  const cachedPanels = Array.from(
    PanelModule.panelsOfClientCache.values()
  ).filter(
    (panel) =>
      !!panel.currentAssignment?.clientId &&
      memberClientIdSet.has(panel.currentAssignment.clientId)
  );

  return observeDevicesOfClients<Panel>(
    "panels",
    memberClientIds,
    onNext,
    cachedPanels
  );
}

export function observePanelsAssignedTo(
  clientId: string,
  userId: string | null | undefined,
  onNext: (panels: Array<Panel>) => void
) {
  const cachedPanels = Array.from(
    PanelModule.panelsOfClientCache.values()
  ).filter((panel) => panel.currentAssignment?.clientId === clientId);

  onNext(cachedPanels);

  if (userId) {
    return db
      .collection("panels")
      .where("currentAssignment.clientId", "==", clientId)
      .where(
        "currentAssignment.currentProjectResponsibleUserIds",
        "array-contains-any",
        [userId]
      )
      .onSnapshot((snapshot) => {
        const panelsOfClient = snapshot.docs.map((doc) => doc.data() as Panel);

        PanelModule.chachePanelsOfClient(panelsOfClient);
        onNext(panelsOfClient);
      });
  } else {
    return db
      .collection("panels")
      .where("currentAssignment.clientId", "==", clientId)
      .onSnapshot((snapshot) => {
        const panelsOfClient = snapshot.docs.map((doc) => doc.data() as Panel);

        PanelModule.chachePanelsOfClient(panelsOfClient);
        onNext(panelsOfClient);
      });
  }
}

export function observePanelsOfMemberClients(
  onNext: (panels: Array<Panel>) => void
) {
  return observePanelsAssignedToMembers(onNext);
}

export function observePanelsOfClient(
  userId: string | null | undefined,
  onNext: (panels: Array<Panel>) => void
) {
  return observePanelsAssignedTo(getClientId(), userId, onNext);
}

/**
 * Observes one Panel record.
 *
 * @param onMissing - called when the server confirms there is no record for this serial number.
 *   Never on a cache-only answer, which is empty before the server has replied.
 */
export function observePanel(
  serialNumber: string,
  onNext: (panel: Panel) => void,
  onMissing?: () => void
) {
  const cachedPanel = PanelModule.cachedPanel(serialNumber);

  if (cachedPanel) {
    onNext(cachedPanel);
  }

  const ref = db.collection("panels").doc(serialNumber);

  return ref.onSnapshot((snapshot) => {
    const panel = snapshot.data() as Panel;

    if (panel) {
      PanelModule.cachePanel(panel);
      onNext(panel);
      return;
    }

    if (!snapshot.metadata.fromCache) {
      onMissing?.();
    }
  });
}
